import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookingLinkCard } from '../components/dashboard/BookingLinkCard';
import { OnboardingCard } from '../components/dashboard/OnboardingCard';
import {
  Users, CalendarDays, MessageSquare, Star, Euro, PhoneCall,
  ChevronRight, CheckCircle2, Clock, CreditCard, TrendingUp, UserPlus,
  XCircle, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const SOURCE_ICON = {
  phone: PhoneCall,
  deposit: CreditCard,
  message: MessageSquare,
  review: Star,
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'adesso';
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ${h === 1 ? 'ora' : 'ore'} fa`;
  const d = Math.floor(h / 24);
  return `${d} ${d === 1 ? 'giorno' : 'giorni'} fa`;
};

const whenLabel = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const DashboardPage = () => {
  const { API, getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`, getAuthHeaders());
      setStats(res.data);
    } catch (error) {
      console.error('Errore nel caricamento statistiche:', error);
    } finally {
      setLoading(false);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/phone/requests/${id}/${action}`, {}, getAuthHeaders());
      toast.success(action === 'approve' ? 'Prenotazione confermata' : 'Richiesta rifiutata');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operazione non riuscita');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const cards = [
    {
      key: 'oggi', title: 'Appuntamenti oggi', value: stats?.appointments_today ?? 0,
      hint: `${stats?.appointments_week ?? 0} nei prossimi 7 giorni`,
      icon: CalendarDays, color: 'text-violet-500', bg: 'bg-violet-500/10', to: '/calendar',
    },
    {
      key: 'da-confermare', title: 'Da confermare', value: stats?.to_confirm ?? 0,
      hint: 'telefono e acconti in sospeso',
      icon: PhoneCall, color: 'text-primary', bg: 'bg-primary/10', to: '/phone',
    },
    {
      key: 'messaggi', title: 'Messaggi non letti', value: stats?.unread_messages ?? 0,
      hint: 'WhatsApp e social', icon: MessageSquare,
      color: 'text-orange-500', bg: 'bg-orange-500/10', to: '/messages',
    },
    {
      key: 'recensioni', title: 'Recensioni da rispondere', value: stats?.pending_reviews ?? 0,
      hint: 'rispondere alza la media', icon: Star,
      color: 'text-yellow-500', bg: 'bg-yellow-500/10', to: '/reviews',
    },
    {
      key: 'clienti', title: 'Clienti', value: stats?.customers_total ?? 0,
      hint: `${stats?.customers_new_month ?? 0} nuovi questo mese`,
      icon: Users, color: 'text-blue-500', bg: 'bg-blue-500/10', to: '/customers',
    },
    {
      key: 'incasso', title: 'Incasso del mese', value: `€${(stats?.revenue_month ?? 0).toFixed(0)}`,
      hint: 'appuntamenti completati', icon: Euro,
      color: 'text-emerald-500', bg: 'bg-emerald-500/10', to: '/calendar',
    },
  ];

  const todo = stats?.todo || [];

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-outfit">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Bentornato! Ecco cosa succede oggi{stats?.salon_name ? ` da ${stats.salon_name}` : ''}.
        </p>
      </div>

      <OnboardingCard />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
        {cards.map((c, i) => (
          <Link key={c.key} to={c.to} data-testid={`stat-${c.key}`}
            className={`block animate-fade-in stagger-${i + 1}`}>
            <Card className="card-hover h-full transition-colors hover:border-primary/40">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${c.bg} flex items-center justify-center`}>
                    <c.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${c.color}`} />
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-xl sm:text-2xl font-bold font-outfit">{c.value}</p>
                <p className="text-[11px] sm:text-xs font-medium mt-0.5 leading-tight">{c.title}</p>
                <p className="text-[10px] sm:text-[11px] text-muted-foreground mt-0.5 leading-tight">{c.hint}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
        <Card className="lg:col-span-7 card-hover">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-outfit flex items-center gap-2">
              Da gestire
              {todo.length > 0 && (
                <span className="text-xs font-normal text-muted-foreground">({todo.length})</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0" data-testid="todo-list">
            {todo.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <CheckCircle2 className="w-10 h-10 text-emerald-500/60 mb-3" />
                <p className="text-sm font-medium">Tutto sotto controllo</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Nessuna prenotazione da confermare, nessun messaggio in sospeso.
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {todo.map((item, i) => {
                  const Icon = SOURCE_ICON[item.source] || Clock;
                  if (item.approvable) {
                    const when = whenLabel(item.start_time);
                    return (
                      <div key={`${item.source}-${item.id}-${i}`}
                        data-testid={`todo-${item.source}-${i}`}
                        className="py-3 -mx-2 px-2 rounded-lg">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                            <Icon className="w-4 h-4 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium leading-snug break-words">{item.label}</p>
                            <p className="text-xs text-muted-foreground mt-0.5 break-words">
                              {[item.customer_name || 'Cliente', item.service_name, when].filter(Boolean).join(' · ')}
                            </p>
                            {item.phone && (
                              <p className="text-xs text-muted-foreground mt-0.5">{item.phone}</p>
                            )}
                            <p className="text-[11px] text-muted-foreground mt-1">{timeAgo(item.time)}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2 pl-11">
                          <Button size="sm" disabled={busyId === item.id}
                            onClick={() => decide(item.id, 'approve')}
                            data-testid={`dash-approve-${item.id}`} className="min-h-[36px]">
                            {busyId === item.id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Conferma</>}
                          </Button>
                          <Button size="sm" variant="outline" disabled={busyId === item.id}
                            onClick={() => decide(item.id, 'reject')}
                            data-testid={`dash-reject-${item.id}`} className="min-h-[36px]">
                            <XCircle className="w-3.5 h-3.5 mr-1" />Rifiuta
                          </Button>
                          <Button size="sm" variant="ghost" asChild className="min-h-[36px]">
                            <Link to="/phone" data-testid={`dash-detail-${item.id}`}>Dettagli</Link>
                          </Button>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <Link key={`${item.source}-${item.id}-${i}`} to={item.link}
                      data-testid={`todo-${item.source}-${i}`}
                      className="flex items-start gap-3 py-3 -mx-2 px-2 rounded-lg transition-colors hover:bg-muted/50">
                      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium leading-snug break-words">{item.label}</p>
                        {item.detail && (
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 break-words">{item.detail}</p>
                        )}
                        <p className="text-[11px] text-muted-foreground mt-1">{timeAgo(item.time)}</p>
                      </div>
                      <span className="text-xs font-medium text-primary shrink-0 mt-1">{item.action}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="lg:col-span-5">
          <BookingLinkCard
            slug={stats?.booking_slug}
            salonName={stats?.salon_name}
            logoUrl={stats?.logo_url ? `${API}${stats.logo_url}` : null}
            accent={stats?.booking_color}
            phone={stats?.salon_phone}
            address={stats?.salon_address}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {[
          { label: 'Nuovo cliente', icon: UserPlus, href: '/customers' },
          { label: 'Nuovo appuntamento', icon: CalendarDays, href: '/calendar' },
          { label: 'Programma post', icon: TrendingUp, href: '/social' },
          { label: 'Assistente IA', icon: MessageSquare, href: '/ai-assistant' },
        ].map((action) => (
          <Button key={action.label} variant="outline" asChild
            className="h-auto py-4 flex flex-col items-center gap-2 card-hover min-h-[44px]">
            <Link to={action.href} data-testid={`quick-${action.href.slice(1)}`}>
              <action.icon className="w-5 h-5" />
              <span className="text-xs sm:text-sm text-center leading-tight">{action.label}</span>
            </Link>
          </Button>
        ))}
      </div>
    </div>
  );
};

export default DashboardPage;
